import React, { useState, useEffect, useMemo } from 'react';
import { Key, LogIn, Shield, Terminal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { ultronTuringApi } from '../services/ultronTuringApi';
import { mcpOAuthService } from '../services/mcpOAuthService';
import { logger } from '../utils/logger';

// Breathing dot component
const BreathingDot: React.FC<{
  size: number;
  left: string;
  top: string;
  delay: number;
  duration: number;
}> = ({ size, left, top, delay, duration }) => (
  <div
    className="absolute rounded-full bg-emerald-400/60"
    style={{
      width: size,
      height: size,
      left,
      top,
      animation: `breathe ${duration}s ease-in-out ${delay}s infinite`,
    }}
  />
);

// Generate random dots configuration
const generateDots = (count: number) => {
  const dots = [];
  for (let i = 0; i < count; i++) {
    dots.push({
      id: i,
      size: Math.random() * 4 + 2, // 2-6px
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      delay: Math.random() * 4, // 0-4s delay
      duration: Math.random() * 2 + 3, // 3-5s duration
    });
  }
  return dots;
};

// Building illustration SVG component
const BuildingIllustration: React.FC = () => (
  <svg
    viewBox="0 0 400 200"
    className="w-full max-w-md opacity-30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Main building */}
    <rect x="80" y="60" width="100" height="140" stroke="url(#buildingGradient)" strokeWidth="1.5" fill="none" />
    {/* Windows */}
    {[0, 1, 2, 3].map(row =>
      [0, 1, 2].map(col => (
        <rect
          key={`window-${row}-${col}`}
          x={95 + col * 25}
          y={75 + row * 30}
          width="15"
          height="20"
          stroke="url(#buildingGradient)"
          strokeWidth="1"
          fill="none"
          opacity="0.6"
        />
      ))
    )}
    
    {/* Side building left */}
    <rect x="20" y="100" width="60" height="100" stroke="url(#buildingGradient)" strokeWidth="1.5" fill="none" />
    {[0, 1, 2].map(row =>
      [0, 1].map(col => (
        <rect
          key={`window-left-${row}-${col}`}
          x={30 + col * 22}
          y={115 + row * 25}
          width="12"
          height="16"
          stroke="url(#buildingGradient)"
          strokeWidth="1"
          fill="none"
          opacity="0.5"
        />
      ))
    )}
    
    {/* Side building right */}
    <rect x="180" y="80" width="80" height="120" stroke="url(#buildingGradient)" strokeWidth="1.5" fill="none" />
    {[0, 1, 2, 3].map(row =>
      [0, 1].map(col => (
        <rect
          key={`window-right-${row}-${col}`}
          x={195 + col * 30}
          y={95 + row * 25}
          width="18"
          height="15"
          stroke="url(#buildingGradient)"
          strokeWidth="1"
          fill="none"
          opacity="0.5"
        />
      ))
    )}
    
    {/* Tower */}
    <rect x="260" y="40" width="50" height="160" stroke="url(#buildingGradient)" strokeWidth="1.5" fill="none" />
    {[0, 1, 2, 3, 4, 5].map(row => (
      <rect
        key={`window-tower-${row}`}
        x={272}
        y={55 + row * 22}
        width="26"
        height="14"
        stroke="url(#buildingGradient)"
        strokeWidth="1"
        fill="none"
        opacity="0.4"
      />
    ))}
    
    {/* Antenna */}
    <line x1="285" y1="40" x2="285" y2="20" stroke="url(#buildingGradient)" strokeWidth="1.5" />
    <circle cx="285" cy="15" r="5" stroke="url(#buildingGradient)" strokeWidth="1" fill="none" />
    
    {/* Small building far right */}
    <rect x="320" y="130" width="60" height="70" stroke="url(#buildingGradient)" strokeWidth="1.5" fill="none" />
    
    {/* Ground line */}
    <line x1="0" y1="200" x2="400" y2="200" stroke="url(#buildingGradient)" strokeWidth="1" opacity="0.3" />
    
    <defs>
      <linearGradient id="buildingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#06b6d4" />
        <stop offset="100%" stopColor="#3b82f6" />
      </linearGradient>
    </defs>
  </svg>
);

// Company Logo component
const CompanyLogo: React.FC<{ size?: 'sm' | 'lg' }> = ({ size = 'lg' }) => {
  const sizeClass = size === 'lg' ? 'w-24 h-28' : 'w-16 h-20';
  return (
    <div className={`${sizeClass} flex items-center justify-center`}>
      <img 
        src="/ultron-company-logo.svg" 
        alt="Ultron Logo" 
        className="w-full h-full object-contain drop-shadow-lg"
        style={{ filter: 'drop-shadow(0 4px 12px rgba(136, 176, 75, 0.4))' }}
      />
    </div>
  );
};

const Login: React.FC = () => {
  const navigate = useNavigate();

  // Setup storage listener for cross-tab OAuth completion
  useEffect(() => {
    mcpOAuthService.setupStorageListener();
  }, []);
  
  const [useManualAuth, setUseManualAuth] = useState(false);
  const [credentials, setCredentials] = useState({
    apiKey: '',
    appId: 'KLMGeDMy'
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Generate dots once on mount
  const dots = useMemo(() => generateDots(30), []);

  // Check if OAuth is enabled
  const isOAuthEnabled = authService.isOAuthEnabled();

  const handleOAuthLogin = () => {
    setIsLoading(true);
    setError('');
    mcpOAuthService.initiateLogin('KLMGeDMy');
  };

  const handleManualLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      const defaultGroupId = process.env.REACT_APP_GROUP_ID || 'rTtAr4wDmmkQBKTYbLBiNB';
      localStorage.setItem('ultron-energy-credentials', JSON.stringify({
        apiKey: credentials.apiKey,
        appId: credentials.appId,
        groupId: defaultGroupId,
        baseUrl: 'https://turing.ultronsmart.com'
      }));

      try {
        const authResult = await ultronTuringApi.checkAuthSession();
        logger.log('🔐 API Key auth result:', authResult);

        if (!authResult.authenticated) {
          logger.error('❌ API Key validation failed');
          localStorage.removeItem('ultron-energy-credentials');
          throw new Error('Invalid credentials');
        }

        if (authResult.authType !== 'apikey') {
          logger.warn('Unexpected auth type:', authResult.authType);
        }

        if (authResult.sessionId) {
          sessionStorage.setItem('session_id', authResult.sessionId);
        }
        if (authResult.tenantId) {
          sessionStorage.setItem('tenant_id', authResult.tenantId);
        }
      } catch (apiError) {
        logger.error('API validation error:', apiError);
        throw new Error('Invalid credentials');
      }

      sessionStorage.setItem('auth_mode', 'apikey');
      logger.log('✅ API Key authentication successful, navigating to dashboard...');
      navigate('/');
    } catch (err) {
      logger.error('❌ Login error:', err);
      localStorage.removeItem('ultron-energy-credentials');
      setError('Invalid API credentials. Please check and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden bg-gradient-to-br from-[#0a1628] via-[#0C2340] to-[#0a1628]">
      {/* CSS for breathing animation */}
      <style>{`
        @keyframes breathe {
          0%, 100% { 
            opacity: 0.2; 
            transform: scale(1);
            box-shadow: 0 0 4px rgba(52, 211, 153, 0.3);
          }
          50% { 
            opacity: 0.9; 
            transform: scale(1.3);
            box-shadow: 0 0 12px rgba(52, 211, 153, 0.6);
          }
        }
      `}</style>

      {/* Breathing dots background */}
      <div className="absolute inset-0 pointer-events-none">
        {dots.map(dot => (
          <BreathingDot
            key={dot.id}
            size={dot.size}
            left={dot.left}
            top={dot.top}
            delay={dot.delay}
            duration={dot.duration}
          />
        ))}
      </div>

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#0a1628]/50 pointer-events-none" />

      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center p-12 relative z-10">
        <div className="max-w-md text-center space-y-8">
          {/* Logo */}
          <div className="flex justify-center">
            <CompanyLogo size="lg" />
          </div>

          {/* Title */}
          <h1 className="text-5xl font-bold text-white tracking-tight">
            IntelliSpace
          </h1>

          {/* Tagline */}
          <p className="text-xl font-medium bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
            Only One Dashboard You Need.
          </p>

          {/* Description */}
          <p className="text-slate-400 text-base leading-relaxed">
            Control HVAC, Lighting, and occupancy systems with
            <br />
            Agentic AI automation. Reduce energy costs by up to{' '}
            <span className="text-cyan-400 font-semibold">45%</span>.
          </p>

          {/* Status indicators */}
          <div className="flex justify-center items-center gap-6 text-sm text-slate-400">
            <div className="flex items-center gap-2">
              <span 
                className="w-2 h-2 rounded-full bg-emerald-400"
                style={{ animation: 'breathe 2s ease-in-out infinite' }}
              />
              <span>Real-time monitoring</span>
            </div>
            <div className="flex items-center gap-2">
              <span 
                className="w-2 h-2 rounded-full bg-emerald-400"
                style={{ animation: 'breathe 2s ease-in-out 0.5s infinite' }}
              />
              <span>Agentic AI: Active</span>
            </div>
          </div>

          {/* Building illustration */}
          <div className="mt-8">
            <BuildingIllustration />
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-8 text-center">
          <p className="text-xs text-slate-500">
            ULTRON © 2026. Powered by Ultron Turing.
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-8 relative z-10">
        {/* Mobile logo (shown only on small screens) */}
        <div className="lg:hidden mb-8 text-center">
          <div className="flex justify-center mb-4">
            <CompanyLogo size="sm" />
          </div>
          <h1 className="text-3xl font-bold text-white">IntelliSpace</h1>
          <p className="text-cyan-400 text-sm mt-2">Only One Dashboard You Need.</p>
        </div>

        {/* Login Card */}
        <div className="w-full max-w-sm">
          <div className="bg-[#0C2340]/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-700/50 p-8 space-y-6">
            {error && (
              <div className="bg-red-900/20 border border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            {!useManualAuth && isOAuthEnabled ? (
              <div className="space-y-5">
                {/* Sign in with ULTRON button */}
                <button
                  onClick={handleOAuthLogin}
                  disabled={isLoading}
                  className="w-full flex justify-center items-center gap-2 py-3.5 px-4 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-medium rounded-xl transition-all duration-300 shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <LogIn className="h-5 w-5" />
                  {isLoading ? 'Redirecting...' : 'Sign in with ULTRON'}
                </button>

                {/* OR divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-600/50"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-[#0C2340] text-slate-500">OR</span>
                  </div>
                </div>

                {/* Get a License button */}
                <button
                  onClick={() => window.location.href = 'https://account.ultroncloud.com/market'}
                  className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-slate-600/50 text-slate-300 font-medium rounded-xl hover:bg-slate-700/30 hover:border-slate-500 transition-all duration-300"
                >
                  <Key className="h-4 w-4" />
                  Get a License
                </button>

                {/* Security note */}
                <div className="flex items-center justify-center gap-2 text-xs text-slate-500 pt-2">
                  <Shield className="h-3.5 w-3.5" />
                  <span>Protected by enterprise-grade security</span>
                </div>
              </div>
            ) : (
              <form className="space-y-5" onSubmit={handleManualLogin}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="apiKey" className="block text-sm font-medium text-slate-300 mb-1.5">
                      API Key
                    </label>
                    <input
                      id="apiKey"
                      name="apiKey"
                      type="password"
                      required
                      value={credentials.apiKey}
                      onChange={(e) => setCredentials({ ...credentials, apiKey: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-600/50 placeholder-slate-500 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 bg-slate-800/50 transition-all"
                      placeholder="Enter your API key"
                    />
                  </div>

                  <div>
                    <label htmlFor="appId" className="block text-sm font-medium text-slate-300 mb-1.5">
                      App ID
                    </label>
                    <select
                      id="appId"
                      name="appId"
                      required
                      value={credentials.appId}
                      onChange={(e) => setCredentials({ ...credentials, appId: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-600/50 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 bg-slate-800/50 transition-all"
                    >
                      <option value="KLMGeDMy">Ultron Unified Boards</option>
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3.5 px-4 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-medium rounded-xl transition-all duration-300 shadow-lg shadow-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Connecting...' : 'Sign in with API Key'}
                </button>

                {isOAuthEnabled && (
                  <button
                    type="button"
                    onClick={() => setUseManualAuth(false)}
                    className="w-full text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                  >
                    Back to OAuth login
                  </button>
                )}
              </form>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default Login;
